import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./Cart.scss";
import { Button, Col, Form, ListGroup, Row } from "react-bootstrap";
import { CartItem } from "./cart-interface";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from '../../contexts/LocalizationContext';
import { jwtDecode } from "jwt-decode";
import { writeLoginSession } from "../../contexts/login.context";

interface Props {
  onCartChange: (fieldName: number, value: number) => void;
  validationErrors: Record<string, string>;
  onPromoChange: (fieldName: string, value: any) => void;
  onPromoSubmit: (value: any) => void;
  onPromoDelete: (value: any) => void;
  isDownload: boolean;
  isVerify: boolean;
  isThanks: boolean;
  cartData: any;
  promoCodeError: boolean;
  conflicts: string[];
}

const Cart: React.FC<Props> = ({ onCartChange, validationErrors, onPromoChange, onPromoSubmit, onPromoDelete, isDownload, isVerify, cartData, isThanks, promoCodeError, conflicts }) => {
  const { showTax } = useLocalization();
  const [subTotal, setSubTotal] = useState("0");
  const [taxTotal, settaxTotal] = useState<string | null>(null)
  const [total, settotal] = useState<string | null>(null)
  const [promo, setPromo] = useState<string>('')
  const [cart, setCart] = useState<CartItem[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isValid, setIsValid] = useState<boolean>(true);
  const [couponApplied, setCouponApplied] = useState<boolean>(false);
  const { state } = useLocation();
  const locale = window.location.pathname.split('/')[1];
  const userType = state ? state.user : sessionStorage.getItem('userType');
  const boldClassName = (!isThanks && !isVerify) ? 'bold' : '';

  const getSubTotalByLocale = (subTotal: string, total: string) => {
    if (['de-de', 'fr-fr', 'it-it', 'es-es', 'en-gb', 'en-eu'].includes(locale) || !subTotal) {
      if (isThanks || isVerify) {
        return subTotal;
      }
      return total;
    }
    return subTotal;
  }

  useEffect(() => {
    let cartState, subTotal;
    cartState = cartData?.["cartItems"]?.["cartItem"] || [];
    subTotal = getSubTotalByLocale(cartData["pricing"]["formattedSubtotal"], cartData["pricing"]["formattedOrderTotal"]);
    const lineItemDiscountApplied = cartState.some((item) => item.pricing.formattedSalePriceWithQuantity !== item.pricing.formattedListPriceWithQuantity);
    const couponsList = Array.isArray(cartData?.coupons)
      ? cartData.coupons
      : (Array.isArray(cartData?.coupon) ? cartData.coupon : []);
    const appliedCoupon = couponsList.find(
      (coupon: any) =>
        coupon?.status === 'applied' ||
        coupon?.is_applied === true ||
        (coupon?.discounted_amount ?? 0) !== 0
    );
    setCouponApplied(lineItemDiscountApplied && Boolean(appliedCoupon));
    setPromo(appliedCoupon?.code ?? '');
    setCart(cartState);
    setSubTotal(subTotal);
    settaxTotal(cartData['pricing']['formattedTax']);
    settotal(cartData['pricing']['formattedOrderTotal']);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, isThanks, isVerify, locale]);
  useEffect(() => {
    setIsValid(!validationErrors?.promoCode);
  }, [validationErrors?.promoCode]);
  const handlePromoChange = useCallback((field: string, val: string) => {
    onPromoChange(field, val);
    setPromo(val);
  }, [onPromoChange]);

  const handlePromoDelete = useCallback((c: boolean) => {
    onPromoDelete(c);
  }, [onPromoDelete]);

  const handlePromoSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onPromoSubmit(promo);
  }, [onPromoSubmit, promo]);

  const [selectedOptions, setSelectedOptions] = useState({});

  const handleOptionChange = useCallback((productId, selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [productId]: selectedOption,
    }));
    onCartChange(productId, selectedOption);
  }, [onCartChange]);

  const handleCartChange = useCallback(async () => {
    console.log('Verify User Page', userType);
    if (cartData?.idt) {
      // Write operation (fire and forget - doesn't return a promise)
      const token = cartData.idt;
      const decoded = jwtDecode(token);
      console.log('idToken decoded===>', decoded);
      const timestamp = decoded['updated_at']; // seconds
      const date = new Date(timestamp * 1000);
      const timestampISO = date.toISOString();
      const writeSession = await writeLoginSession(decoded['email'], decoded['idp_id'], timestampISO, decoded['sub'] || '');
      console.log('writeSession===>', writeSession);
      navigate(`/${locale}/software/cart/user/loggedIn`);
    } else {
      navigate(`/${locale}/software/cart/user/guest`)
    }
  }, [userType, navigate, locale, cartData?.idt]);

  const thanksClassName = isThanks ? 'cartThanks' : '';

  const localeClass = useMemo(() => {
    if (locale === 'en-us') {
      return 'us';
    } else if (locale === 'de-de') {
      return 'germany';
    } else if (locale === 'fr-fr') {
      return 'france';
    } else if (locale === 'it-it') {
      return 'italy';
    } else if (locale === 'es-es') {
      return 'spain';
    } else {
      return 'britain';
    }
  }, [locale]);

  const borderLineClassnames = (!isThanks && !isVerify) ? 'borderLine' : '';
  const useIndiaExTaxPrices =
    locale === 'en-in' && Number(cartData?.pricing?.tax?.value) === 0;
  const getLinePrice = (
    item: any,
    inclusiveField: 'formattedListPrice' | 'formattedListPriceWithQuantity',
    exTaxField: 'formattedListPriceExTax' | 'formattedListPriceExTaxWithQuantity',
  ) => (
    useIndiaExTaxPrices
      ? item?.pricing?.[exTaxField] || item?.pricing?.[inclusiveField]
      : item?.pricing?.[inclusiveField]
  );
  const shouldShowIndiaTaxIncluded = (item: any) =>
    locale === 'en-in' &&
    (!useIndiaExTaxPrices || !item?.pricing?.formattedListPriceExTaxWithQuantity);
  let cartHeading;
  if (isDownload) {
    cartHeading = <h3 id="cartHeading">{t('SHOPPING_CART_HEADER')}</h3>;
  } else if (isVerify) {
    cartHeading = (
      <div className="cartHeading">
        <h3 id="cartHeading">{t("ORDER_SUMMARY")}</h3>
        <button
          type="button"
          onClick={handleCartChange}
          className="continue"
          aria-label={t('BTN_EDIT_CAPS')}
        >
          {t('BTN_EDIT_CAPS')}
        </button>
      </div>
    );
  } else {
    cartHeading = (
      <div className="cartHeading">
        <h3 id="cartHeading">{t("ORDER_SUMMARY")}</h3>
      </div>
    );
  }
  return (
    <div className="cartDetails" role="region" aria-labelledby="cartHeading">
      <Row xs={'auto'} md={'auto'} className="dr_cart_title mt-4">
        <Col md={6} sm={6} className="p-0">
          {cartHeading}
        </Col>
      </Row>
      {validationErrors?.enrollmentErr && (
        <Row xs={'auto'} md={'auto'}>
          <Col md={12} sm={12} className="p-0">
            <div className="invalid-feedback d-block" id="enrollmentErr" aria-live="polite">
              {validationErrors.enrollmentErr}
              <ul>
                {conflicts.map((conflict: any) => (
                  <li key={conflict.course_id}>{conflict.name}</li>
                ))}
              </ul>
            </div>
          </Col>
        </Row>
      )}
      {validationErrors?.isNPNUserQuantityLimit && (
        <Row xs={'auto'} md={'auto'}>
          <Col md={12} sm={12} className="p-0">
            <div className="invalid-feedback d-block" id="isNPNUserQuantityLimit" aria-live="polite">
              {validationErrors.isNPNUserQuantityLimit}
            </div>
          </Col>
        </Row>
      )}
      {cart?.length && (
        <Row xs={'auto'} md={'auto'} className="mb-4">
          <Col md={12} sm={12}>
            <ListGroup className={`digitalCart orderItems`}>
              {cart.map((item: any, i: number) => (
                <ListGroup.Item className={`${thanksClassName} items ${borderLineClassnames}`} key={item["product"]["alm_id"]} role="group" aria-labelledby={`productTitle-${item["product"]["alm_id"]}`}>
                  <Form className="orderItemForm" >
                    <div className={`itemDetails`}>
                      <div className="productTitle" id={`productTitle-${item["product"]["alm_id"]}`}>
                        <span className="title">
                          {item["product"]["displayName"]}
                        </span>
                        {
                          cartData.category === "ACADEMY" ? (
                            <span className="academy-access">
                              1 Year Access
                            </span>
                          ) : <span />
                        }
                        {item["quantity"] ? (isThanks || isVerify) && <p aria-label={`${t('INVOICE_PRODUCT_QUANTITY')} ${item["quantity"]}`}>{t('INVOICE_PRODUCT_QUANTITY')} {item["quantity"]}</p> : ''}
                      </div>
                      {item["quantity"] && (
                        <div className="quantitySelector">
                          {(!isThanks && !isVerify) && (
                            <>
                              <Form.Group className="orderQuantity">
                                {(isDownload || item["product"]["maximumQuantity"] > 1) ? (
                                  <>
                                    <span
                                      id={`label-shopperQuantity-${item["product"]["alm_id"]}`}
                                      className="visually-hidden"
                                    >
                                      {t('INVOICE_PRODUCT_QUANTITY_COLON')} {item["product"]["displayName"]}
                                    </span>
                                    <Form.Select
                                      autoComplete="none"
                                      aria-labelledby={`label-shopperQuantity-${item["product"]["alm_id"]}`}
                                      value={selectedOptions[item.id] || item["quantity"]}
                                      onChange={(e) =>
                                        handleOptionChange(
                                          item["product"]["alm_id"],
                                          parseInt(e.target.value, 10)
                                        )
                                      }
                                      id={`shopperQuantity-${item["product"]["alm_id"]}`}
                                    >
                                      {[
                                        ...Array(item["product"]["maximumQuantity"]).keys(),
                                      ].map((index) => (
                                        <option key={index} value={index + 1}>
                                          {index + 1}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </>
                                ) : (
                                  <p aria-label={`${t('INVOICE_PRODUCT_QUANTITY')} ${item["quantity"]}`}>{t('INVOICE_PRODUCT_QUANTITY')} {item["quantity"]}</p>
                                )}
                              </Form.Group>
                              <span className="continue productRemove" onClick={(e) => handleOptionChange(item["product"]["alm_id"], item["quantity"] ? 0 : -1)}>{t('BTN_REMOVE')}</span>
                            </>
                          )}
                        </div>
                      )}
                      {item["quantity"] ? (<div className="itemPrice">
                        <div className="productPrice">
                          <p className={`${boldClassName} price ${shouldShowIndiaTaxIncluded(item) ? 'taxIncludedPrice' : ''}`}>
                            <span>{getLinePrice(item, 'formattedListPriceWithQuantity', 'formattedListPriceExTaxWithQuantity') || 0}</span>
                            {shouldShowIndiaTaxIncluded(item) && (
                              <span className="taxations">{t('LINE_ITEM_TAX_INCLUDED')}</span>
                            )}
                          </p>
                          {!isThanks && !isVerify && item && item["quantity"] > 1 && (
                            <p>
                              (
                              {getLinePrice(item, 'formattedListPrice', 'formattedListPriceExTax') || 0} {t('EACH')})
                            </p>
                          )}
                        </div>
                      </div>) : <button type="button" className="cart__remove__button" onClick={(e) => handleOptionChange(item["product"]["alm_id"], item["quantity"] ? 0 : -1)} aria-label={`${t('BTN_REMOVE')} ${item["product"]["displayName"]}`}></button>}
                    </div>
                  </Form>
                  {cartData.npn_partner_user && item.isExternalIlt && <p className="external-ilt-message">{t('IS_EXTERNAL_ILT')}</p>}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        </Row>
      )}
      <Row xs={'1'} md={'1'} className="cartPricing">
        {(!isThanks && !isVerify) && (
          <Col md={isDownload ? 6 : 12} className="p-0">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="promoCode" className="visually-hidden">
                {t('COUPON_CODE_TEXT')}
              </Form.Label>
              <span
                id="dr_promoCouponCodeNew"
                className={(!isValid && promoCodeError) ? 'is-invalid' : ''}
              >
                {/* REMOVE htmlFor to avoid double-labeling */}
                <Form.Label className="visually-hidden" id="promoCodeLabel">
                  {t('COUPON_CODE_TEXT_COLON')}
                </Form.Label>

                <Form.Control
                  type="text"
                  autoComplete="none"
                  name="couponCode"
                  value={promo}
                  id="promoCodeField"
                  placeholder={t('COUPON_CODE_TEXT')}
                  className="mb-2"
                  isInvalid={!!validationErrors?.promoCode}
                  aria-invalid={!!validationErrors?.promoCode}
                  aria-labelledby="promoCodeLabel"
                  aria-describedby={validationErrors?.promoCode ? 'promoCodeError' : undefined}
                  onBlur={(e) => handlePromoChange('promoCode', e.target.value)}
                  onChange={(e) => handlePromoChange('promoCode', e.target.value)}
                />

                <Button
                  type="button"
                  id="promoDR_Button"
                  className="dr_button"
                  onClick={handlePromoSubmit}
                  aria-label={`${t('BTN_APPLY')} ${t('COUPON_CODE_TEXT_COLON')}`}
                >
                  {t("BTN_APPLY")}
                </Button>
              </span>
              {validationErrors?.promoCode && (
                <Form.Control.Feedback type="invalid" id="promoCodeError" aria-live="polite">
                  {validationErrors.promoCode}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        )}
        <Col md={isDownload ? 6 : 12} className="p-0 borderLine">
          {cartData["npn_partner_user"] && cartData.category === "ACADEMY" &&
            (cartData?.['coupons']?.find((coupon: any) => (coupon.status === 'applied' && (coupon?.coupon_type || '').toUpperCase() === 'NPN_PARTNER_PROMOTION')) ?
              (<p className="partner-discount-message">{t('NPN_DICOUNT_APPLIED')}</p>) :
              (cartData?.['coupons']?.find((coupon: any) => (coupon.status === 'applied')) &&
                (<p className="partner-discount-message">{t('NO_PARTNER_DISCOUNTS')}</p>)
              )
            )
          }
        </Col>
        <Col md={isDownload ? 6 : 12} className="pricings p-0">
          {(isThanks || isVerify) &&
            <>
              <p>
                <span>{t('nvidiaSI_SUBTOTAL_HEADING')}:</span>
                <span className="dr_price dr_totals">{subTotal}</span>
              </p>
              {locale !== 'en-af' && locale !== 'es-la' && locale !== 'es-mx' && locale !== 'pt-br' && locale !== 'en-hk' && locale !== 'en-me' && showTax && taxTotal && (
                <p className="tax">
                  <span>{t('TAX')}</span>
                  <span className="dr_price dr_totals">{taxTotal}</span>
                </p>
              )}
            </>
          }
          {(!isThanks && !isVerify) && (
            <>
              {couponApplied && (
                <p className="couponDiscount">
                  <span className="couponHeading">{t('DISCOUNT_COUPON')}:</span>
                  <span className="dr_price dr_totals">
                    <label htmlFor="couponRemove" className="couponRemove"><p id="couponRemove" onClick={() => handlePromoDelete(couponApplied)}>{t('BTN_REMOVE_CODE')}</p></label>
                  </span>
                </p>
              )}
              <p className="total pt-2">
                <span className="subTotalHeading">{(locale === 'en-us' || locale === 'en-ca' || locale === 'nb-no' || locale === 'en-ap') ? t('nvidiaSI_SUBTOTAL_HEADING') : t('nvidiaSI_TOTAL_HEADING')}</span>
                {t('GENERIC_TAX_VERB') === 'GENERIC_TAX_VERB' && (
                  <>
                    {locale !== 'pl-pl' && locale !== 'en-in' && (
                      (locale === 'en-us' || locale === 'en-ca' || locale !== 'en-ap') ? (
                        <span className="taxations">&nbsp;({t('TAX_NOT_INCLUDED').toLowerCase()})</span>
                      ) : (
                        <span className="taxations">&nbsp;{t('VAT_INCLUDED_DIGITAL')}</span>
                      )
                    )}
                  </>
                )}:&#160;
                <span className={`${localeClass} dr_price dr_totals`}>{locale === 'en-us' || locale === 'en-ca' || locale === 'nb-no' ? subTotal : total}</span>
              </p>
            </>
          )}
          {(isThanks || isVerify) && total && (
            <p className="priceTotal pt-2">
              <span className="cartTotals">{t('nvidiaSI_TOTAL_HEADING')}</span>
              {((isVerify || locale === 'en-eu' || locale === 'es-es' || locale === 'fr-fr' || locale === 'it-it' || locale === 'de-de') &&
                locale !== 'en-in' && locale !== 'en-ap' && locale !== 'en-us' && locale !== 'en-ca' && locale !== 'ja-jp') && t('GENERIC_TAX_VERB') === 'GENERIC_TAX_VERB' && (
                  <>
                    {locale !== 'pl-pl' && (
                      <span className="taxations">&nbsp;{t('VAT_INCLUDED_DIGITAL')}</span>
                    )}
                  </>
                )}:&#160;
              <span className={`${localeClass} dr_price dr_totals`}>{total}</span>
            </p>
          )}
        </Col>
        {t('GENERIC_TAX_VERB') !== 'GENERIC_TAX_VERB' && <Col md="12" className="pricings p-0">({t('GENERIC_TAX_VERB')})</Col>}
      </Row>
    </div >
  );
};

export default React.memo(Cart);
